/**
 * WalletConnect session hygiene helpers
 * =====================================
 * Pure utilities used by useUnifiedWallet to fully tear down WC sessions
 * and force a fresh pairing when the user disconnects and reconnects
 * with a different wallet.
 *
 * WC v2 persists under browser storage key prefixes (see ethereum-provider /
 * universal-provider / sign-client / core). Calling provider.disconnect()
 * alone is not enough if storage is left behind — the next EthereumProvider.init
 * can loadPersistedSession() and reuse the old pairing.
 */

/** Storage key prefixes written by WalletConnect v2 client stack */
export const WC_STORAGE_KEY_PREFIXES = [
  'wc@2:ethereum_provider:',
  'wc@2:universal_provider:',
  'wc@2:client:',
  'wc@2:core:',
] as const;

/**
 * App-owned marker: user (or remote session end) intentionally left a disconnected
 * state. Prevents silent restore and signals the next WC connect to wipe first.
 */
export const INTENTIONAL_DISCONNECT_KEY = 'sportsfuture:wallet:intentionalDisconnect';

/** Minimal storage interface (localStorage / sessionStorage / in-memory mock) */
export interface StorageLike {
  length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getBrowserLocalStorage(): StorageLike | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage;
}

function getBrowserSessionStorage(): StorageLike | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  return window.sessionStorage;
}

/**
 * Returns true if a storage key belongs to WalletConnect v2 client state.
 */
export function isWalletConnectStorageKey(key: string): boolean {
  return WC_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Remove all WC v2 keys from a storage object.
 * @returns number of keys removed
 */
export function clearWalletConnectKeysFromStorage(storage: StorageLike | null | undefined): number {
  if (!storage) return 0;

  const toRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && isWalletConnectStorageKey(key)) {
      toRemove.push(key);
    }
  }

  for (const key of toRemove) {
    try {
      storage.removeItem(key);
    } catch {
      // ignore per-key failures (quota / private mode edge cases)
    }
  }

  return toRemove.length;
}

/**
 * Wipe residual WalletConnect session/pairing storage from local + session storage.
 * Safe to call when no provider is live (e.g. before a fresh connect).
 * @returns total keys removed across both stores
 */
export function clearWalletConnectStorage(
  localStorageLike: StorageLike | null | undefined = getBrowserLocalStorage(),
  sessionStorageLike: StorageLike | null | undefined = getBrowserSessionStorage()
): number {
  return (
    clearWalletConnectKeysFromStorage(localStorageLike) +
    clearWalletConnectKeysFromStorage(sessionStorageLike)
  );
}

export function setIntentionalDisconnectMarker(
  storage: StorageLike | null | undefined = getBrowserLocalStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(INTENTIONAL_DISCONNECT_KEY, '1');
  } catch {
    // non-critical
  }
}

export function clearIntentionalDisconnectMarker(
  storage: StorageLike | null | undefined = getBrowserLocalStorage()
): void {
  if (!storage) return;
  try {
    storage.removeItem(INTENTIONAL_DISCONNECT_KEY);
  } catch {
    // non-critical
  }
}

export function hasIntentionalDisconnectMarker(
  storage: StorageLike | null | undefined = getBrowserLocalStorage()
): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(INTENTIONAL_DISCONNECT_KEY) === '1';
  } catch {
    return false;
  }
}

/** Minimal provider shape for WC teardown */
export interface DisconnectableProvider {
  disconnect?: () => Promise<void>;
  session?: unknown;
}

/**
 * Kill a live WC provider session (if any) and wipe client storage so the next
 * EthereumProvider.init cannot restore the old pairing.
 */
export async function teardownWalletConnectProvider(
  provider: DisconnectableProvider | null | undefined,
  localStorageLike: StorageLike | null | undefined = getBrowserLocalStorage(),
  sessionStorageLike: StorageLike | null | undefined = getBrowserSessionStorage()
): Promise<void> {
  if (provider?.disconnect) {
    try {
      await provider.disconnect();
    } catch (error) {
      console.warn('WC disconnect error (non-critical):', error);
    }
  }

  clearWalletConnectStorage(localStorageLike, sessionStorageLike);
}
